import type { AnnotationSide, FileContents, LineDiffTypes } from "@pierre/diffs"
import { type ParsedPatch, File as PierreFile, FileDiff as PierreFileDiff, MultiFileDiff as PierreMultiFileDiff } from "@pierre/diffs/react"
import { Check, Copy } from "lucide-react"
import { observer } from "mobx-react"
/**
 * FilesAndDiffs - Unified wrappers for pierre-diffs components
 *
 * This module provides themed wrappers around @pierre/diffs components with:
 * - Automatic dark/light theme synchronization via DOM detection
 * - Optional comment support via explicit `commentHandlers` prop
 *
 * Usage:
 * - For read-only display: omit commentHandlers or pass null
 * - For commenting support: pass commentHandlers object
 */
import { type ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Z_INDEX } from "../constants"
import type { Comment, CommentSelectedText, CommentSource } from "../types"
import { copyTextToClipboard } from "../utils/clipboard"
import { DiffErrorBoundary } from "./DiffErrorBoundary"
import { useCommentAnnotations } from "./comments/hooks/useCommentAnnotations"
import type { CommentAnnotationMeta } from "./comments/types"
import { extractSelectedText, extractSelectedTextFromDiff, getFileDiffCopyContent, isLineInDiffHunks, normalizeFileDiffForRender } from "./comments/utils"
import { formatMarkdownTables, shouldFormatAsMarkdown } from "./utils/markdownTableFormatter"

// ============================================================================
// Theme Detection
// ============================================================================

/**
 * Detects editor theme by reading --editor-theme CSS variable from the ref element.
 * The variable is inherited from the nearest ancestor with the .code-theme class.
 * Watches for class changes on that ancestor to detect theme switches.
 * Returns the literal theme name (e.g., "pierre-dark", "pierre-light", "tokyo-night").
 */
function useEditorTheme(ref: React.RefObject<HTMLElement | null>): string | null {
    const [theme, setTheme] = useState<string | null>(null)

    useLayoutEffect(() => {
        const el = ref.current
        if (!el) return

        const updateTheme = () => {
            const computed = getComputedStyle(el).getPropertyValue("--editor-theme").trim()
            setTheme(computed || "pierre-dark")
        }

        // Initial read
        updateTheme()

        // Find the nearest ancestor with the .code-theme class to observe
        const themeAncestor = el.closest(".code-theme")
        if (!themeAncestor) return

        const observer = new MutationObserver(updateTheme)
        observer.observe(themeAncestor, {
            attributes: true,
            attributeFilter: ["class"],
        })

        return () => observer.disconnect()
    }, [ref])

    return theme
}

// ============================================================================
// Copy Overlay Component
// ============================================================================

interface CopyOverlayProps {
    content: string
    label?: string
    children: ReactNode
}

function CopyOverlay({ content, label = "Copy to clipboard", children }: CopyOverlayProps) {
    const [copied, setCopied] = useState(false)

    const handleCopy = async () => {
        try {
            await copyTextToClipboard(content)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error("Failed to copy:", err)
        }
    }

    return (
        <div className="group/copy relative">
            {children}
            <button
                type="button"
                onClick={handleCopy}
                className="absolute top-2 right-2 flex items-center justify-center p-1.5 bg-base-300/80 hover:bg-base-300 text-muted hover:text-base-content cursor-pointer opacity-0 group-hover/copy:opacity-100 [@media(hover:none)_and_(pointer:coarse)]:opacity-100 transition-opacity"
                style={{ zIndex: Z_INDEX.COPY_OVERLAY }}
                aria-label={label}
                title={label}
            >
                {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
            </button>
        </div>
    )
}

// ============================================================================
// Comment Handlers Type
// ============================================================================

export interface CommentHandlers {
    taskId: string
    sourceMatch: (comment: Comment) => boolean
    createSource: (lineStart: number, lineEnd: number, side: AnnotationSide) => CommentSource
    /** When true, comments are displayed but can't be added/edited/deleted */
    readOnly?: boolean
}

// ============================================================================
// File Component
// ============================================================================

type DiffStyle = "split" | "unified"

interface FileViewerProps {
    file: FileContents
    className?: string
    disableFileHeader?: boolean
    disableLineNumbers?: boolean
    overflow?: "scroll" | "wrap"
    formatMarkdownContent?: boolean
    /** Pass handlers to enable commenting, or null for read-only display */
    commentHandlers: CommentHandlers | null
    /** Optional line range to highlight (e.g., for search results) */
    highlightLines?: { start: number; end: number } | null
    /** Content for the copy button. Defaults to file.contents. */
    copyContent?: string
    copyLabel?: string
}

export const FileViewer = observer(function FileViewer({
    file,
    className,
    disableFileHeader,
    disableLineNumbers,
    overflow = "wrap",
    formatMarkdownContent = true,
    commentHandlers,
    highlightLines,
    copyContent,
    copyLabel,
}: FileViewerProps) {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const theme = useEditorTheme(wrapperRef)

    // Format markdown tables for better display
    const formattedFile = useMemo((): FileContents => {
        if (!formatMarkdownContent || !shouldFormatAsMarkdown(file.name, file.lang)) {
            return file
        }
        return {
            ...file,
            contents: formatMarkdownTables(file.contents),
        }
    }, [file, formatMarkdownContent])

    // Pierre paints its own themed surface. Do not mount it with a guessed
    // dark theme before a light app has resolved its inherited CSS token.
    if (!theme) return <div ref={wrapperRef} className={className} />

    if (commentHandlers === null) {
        return (
            <div ref={wrapperRef}>
                <CopyOverlay content={copyContent ?? file.contents} label={copyLabel}>
                    <PierreFile
                        file={formattedFile}
                        className={className}
                        options={{
                            theme,
                            overflow,
                            disableFileHeader,
                            disableLineNumbers,
                            enableLineSelection: false,
                            enableGutterUtility: false,
                        }}
                        selectedLines={highlightLines}
                    />
                </CopyOverlay>
            </div>
        )
    }

    return (
        <div ref={wrapperRef}>
            <CommentableFileInner
                file={formattedFile}
                className={className}
                disableFileHeader={disableFileHeader}
                disableLineNumbers={disableLineNumbers}
                commentHandlers={commentHandlers}
                theme={theme}
                overflow={overflow}
                highlightLines={highlightLines}
                copyLabel={copyLabel}
            />
        </div>
    )
})

interface CommentableFileInnerProps {
    file: FileContents
    className?: string
    disableFileHeader?: boolean
    disableLineNumbers?: boolean
    commentHandlers: CommentHandlers
    theme: string
    overflow: "scroll" | "wrap"
    highlightLines?: { start: number; end: number } | null
    copyLabel?: string
}

const CommentableFileInner = observer(function CommentableFileInner({
    file,
    className,
    disableFileHeader,
    disableLineNumbers,
    commentHandlers,
    theme,
    overflow,
    highlightLines,
    copyLabel,
}: CommentableFileInnerProps) {
    const { taskId, sourceMatch, createSource, readOnly = false } = commentHandlers
    const contentDeps = useMemo(() => [file], [file])

    const getSelectedText = useCallback(
        (lineStart: number, lineEnd: number, _side: AnnotationSide): CommentSelectedText => {
            return extractSelectedText(file.contents, lineStart, lineEnd)
        },
        [file.contents]
    )

    const { lineAnnotations, selectedRange, hasOpenForm, handleLineSelectionEnd, renderAnnotation } = useCommentAnnotations({
        taskId,
        sourceMatch,
        createSource,
        getSelectedText,
        contentDeps,
        isDiffView: false,
        defaultSide: "additions",
        readOnly,
    })

    // Merge highlightLines with selectedRange (comment annotations take precedence)
    const mergedSelectedLines = useMemo(() => {
        if (selectedRange) return selectedRange
        return highlightLines
    }, [selectedRange, highlightLines])

    return (
        <CopyOverlay content={file.contents} label={copyLabel}>
            <PierreFile<CommentAnnotationMeta>
                file={file}
                className={className}
                options={{
                    theme,
                    overflow,
                    disableFileHeader,
                    disableLineNumbers,
                    enableLineSelection: false,
                    enableGutterUtility: !readOnly && !hasOpenForm,
                    onGutterUtilityClick: !readOnly && !hasOpenForm ? (range) => handleLineSelectionEnd(range) : undefined,
                }}
                selectedLines={mergedSelectedLines}
                lineAnnotations={lineAnnotations}
                renderAnnotation={renderAnnotation}
            />
        </CopyOverlay>
    )
})

// ============================================================================
// FileDiff Component
// ============================================================================

interface FileDiffViewerProps {
    fileDiff: ParsedPatch["files"][number]
    className?: string
    diffStyle?: DiffStyle
    disableFileHeader?: boolean
    disableWorkerPool?: boolean
    options?: {
        lineDiffType?: LineDiffTypes
        maxLineDiffLength?: number
        tokenizeMaxLineLength?: number
        overflow?: "scroll" | "wrap"
    }
    /** Pass handlers to enable commenting, or null for read-only display */
    commentHandlers: CommentHandlers | null
}

export const FileDiffViewer = observer(function FileDiffViewer({
    fileDiff,
    className,
    diffStyle = "split",
    disableFileHeader,
    disableWorkerPool,
    options,
    commentHandlers,
}: FileDiffViewerProps) {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const theme = useEditorTheme(wrapperRef)

    const copyContent = getFileDiffCopyContent(fileDiff)
    const renderFileDiff = useMemo(() => normalizeFileDiffForRender(fileDiff), [fileDiff])

    if (!theme) return <div ref={wrapperRef} className={className} />

    if (commentHandlers === null) {
        return (
            <div ref={wrapperRef}>
                <DiffErrorBoundary fileName={fileDiff.name}>
                    <CopyOverlay content={copyContent}>
                        <PierreFileDiff
                            fileDiff={renderFileDiff}
                            className={className}
                            disableWorkerPool={disableWorkerPool}
                            options={{
                                overflow: "wrap",
                                diffStyle,
                                disableFileHeader,
                                enableLineSelection: false,
                                enableGutterUtility: false,
                                ...options,
                                theme,
                            }}
                        />
                    </CopyOverlay>
                </DiffErrorBoundary>
            </div>
        )
    }

    return (
        <div ref={wrapperRef}>
            <DiffErrorBoundary fileName={fileDiff.name}>
                <CommentableFileDiffInner
                    fileDiff={fileDiff}
                    renderFileDiff={renderFileDiff}
                    className={className}
                    diffStyle={diffStyle}
                    disableFileHeader={disableFileHeader}
                    disableWorkerPool={disableWorkerPool}
                    options={options}
                    commentHandlers={commentHandlers}
                    theme={theme}
                />
            </DiffErrorBoundary>
        </div>
    )
})

interface CommentableFileDiffInnerProps {
    fileDiff: ParsedPatch["files"][number]
    renderFileDiff: ParsedPatch["files"][number]
    className?: string
    diffStyle: DiffStyle
    disableFileHeader?: boolean
    disableWorkerPool?: boolean
    options?: {
        lineDiffType?: LineDiffTypes
        maxLineDiffLength?: number
        tokenizeMaxLineLength?: number
        overflow?: "scroll" | "wrap"
    }
    commentHandlers: CommentHandlers
    theme: string
}

const CommentableFileDiffInner = observer(function CommentableFileDiffInner({
    fileDiff,
    renderFileDiff,
    className,
    diffStyle,
    disableFileHeader,
    disableWorkerPool,
    options,
    commentHandlers,
    theme,
}: CommentableFileDiffInnerProps) {
    const { taskId, sourceMatch, createSource, readOnly = false } = commentHandlers
    const contentDeps = useMemo(() => [fileDiff], [fileDiff])

    const isLineValid = useCallback((lineNumber: number, side: AnnotationSide) => isLineInDiffHunks(fileDiff, lineNumber, side), [fileDiff])

    const getSelectedText = useCallback(
        (lineStart: number, lineEnd: number, side: AnnotationSide): CommentSelectedText => {
            return extractSelectedTextFromDiff(fileDiff, lineStart, lineEnd, side)
        },
        [fileDiff]
    )

    const { diffLineAnnotations, selectedRange, hasOpenForm, handleLineSelectionEnd, renderAnnotation } = useCommentAnnotations({
        taskId,
        sourceMatch,
        createSource,
        getSelectedText,
        contentDeps,
        isLineValid,
        isDiffView: true,
        defaultSide: "additions",
        readOnly,
    })

    const copyContent = getFileDiffCopyContent(fileDiff)

    return (
        <CopyOverlay content={copyContent}>
            <PierreFileDiff<CommentAnnotationMeta>
                fileDiff={renderFileDiff}
                className={className}
                disableWorkerPool={disableWorkerPool}
                options={{
                    overflow: "wrap",
                    diffStyle,
                    disableFileHeader,
                    ...options,
                    theme,
                    enableLineSelection: false,
                    enableGutterUtility: !readOnly && !hasOpenForm,
                    onGutterUtilityClick: !readOnly && !hasOpenForm ? (range) => handleLineSelectionEnd(range) : undefined,
                }}
                selectedLines={selectedRange}
                lineAnnotations={diffLineAnnotations}
                renderAnnotation={renderAnnotation}
            />
        </CopyOverlay>
    )
})

// ============================================================================
// MultiFileDiff Component
// ============================================================================

interface MultiFileDiffViewerProps {
    oldFile: FileContents
    newFile: FileContents
    className?: string
    diffStyle?: DiffStyle
    expandUnchanged?: boolean
    expansionLineCount?: number
    disableFileHeader?: boolean
    /** Pass handlers to enable commenting, or null for read-only display */
    commentHandlers: CommentHandlers | null
}

export const MultiFileDiffViewer = observer(function MultiFileDiffViewer({
    oldFile,
    newFile,
    className,
    diffStyle = "split",
    expandUnchanged,
    expansionLineCount,
    disableFileHeader,
    commentHandlers,
}: MultiFileDiffViewerProps) {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const theme = useEditorTheme(wrapperRef)

    // Format markdown tables for better display
    const formattedOldFile = useMemo((): FileContents => {
        if (!shouldFormatAsMarkdown(oldFile.name, oldFile.lang)) {
            return oldFile
        }
        return {
            ...oldFile,
            contents: formatMarkdownTables(oldFile.contents),
        }
    }, [oldFile])

    const formattedNewFile = useMemo((): FileContents => {
        if (!shouldFormatAsMarkdown(newFile.name, newFile.lang)) {
            return newFile
        }
        return {
            ...newFile,
            contents: formatMarkdownTables(newFile.contents),
        }
    }, [newFile])

    if (!theme) return <div ref={wrapperRef} className={className} />

    if (commentHandlers === null) {
        return (
            <div ref={wrapperRef}>
                <CopyOverlay content={newFile.contents}>
                    <PierreMultiFileDiff
                        oldFile={formattedOldFile}
                        newFile={formattedNewFile}
                        className={className}
                        options={{
                            theme,
                            overflow: "wrap",
                            diffStyle,
                            expandUnchanged,
                            expansionLineCount,
                            disableFileHeader,
                            enableLineSelection: false,
                            enableGutterUtility: false,
                        }}
                    />
                </CopyOverlay>
            </div>
        )
    }

    return (
        <div ref={wrapperRef}>
            <CommentableMultiFileDiffInner
                oldFile={formattedOldFile}
                newFile={formattedNewFile}
                className={className}
                diffStyle={diffStyle}
                expandUnchanged={expandUnchanged}
                expansionLineCount={expansionLineCount}
                disableFileHeader={disableFileHeader}
                commentHandlers={commentHandlers}
                theme={theme}
            />
        </div>
    )
})

interface CommentableMultiFileDiffInnerProps {
    oldFile: FileContents
    newFile: FileContents
    className?: string
    diffStyle: DiffStyle
    expandUnchanged?: boolean
    expansionLineCount?: number
    disableFileHeader?: boolean
    commentHandlers: CommentHandlers
    theme: string
}

const CommentableMultiFileDiffInner = observer(function CommentableMultiFileDiffInner({
    oldFile,
    newFile,
    className,
    diffStyle,
    expandUnchanged,
    expansionLineCount,
    disableFileHeader,
    commentHandlers,
    theme,
}: CommentableMultiFileDiffInnerProps) {
    const { taskId, sourceMatch, createSource, readOnly = false } = commentHandlers
    const contentDeps = useMemo(() => [oldFile, newFile], [oldFile, newFile])

    const getSelectedText = useCallback(
        (lineStart: number, lineEnd: number, side: AnnotationSide): CommentSelectedText => {
            const contents = side === "deletions" ? oldFile.contents : newFile.contents
            return extractSelectedText(contents, lineStart, lineEnd)
        },
        [oldFile.contents, newFile.contents]
    )

    const { diffLineAnnotations, selectedRange, hasOpenForm, handleLineSelectionEnd, renderAnnotation } = useCommentAnnotations({
        taskId,
        sourceMatch,
        createSource,
        getSelectedText,
        contentDeps,
        isDiffView: true,
        defaultSide: "additions",
        readOnly,
    })

    return (
        <CopyOverlay content={newFile.contents}>
            <PierreMultiFileDiff<CommentAnnotationMeta>
                oldFile={oldFile}
                newFile={newFile}
                className={className}
                options={{
                    theme,
                    overflow: "wrap",
                    diffStyle,
                    expandUnchanged,
                    expansionLineCount,
                    disableFileHeader,
                    enableLineSelection: false,
                    enableGutterUtility: !readOnly && !hasOpenForm,
                    onGutterUtilityClick: !readOnly && !hasOpenForm ? (range) => handleLineSelectionEnd(range) : undefined,
                }}
                selectedLines={selectedRange}
                lineAnnotations={diffLineAnnotations}
                renderAnnotation={renderAnnotation}
            />
        </CopyOverlay>
    )
})

// ============================================================================
// Re-exports for convenience
// ============================================================================

export { parsePatchFiles } from "@pierre/diffs"
export type { ParsedPatch } from "@pierre/diffs/react"
export type { AnnotationSide } from "@pierre/diffs"
